// src/components/themes/default/admin/BranchDashboard.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import BranchSidebar from "./BranchSidebar";
import BranchOverviewTab from "./BranchOverviewTab";
import AdminHeader from "@/components/admin/AdminHeader";
import SettingsTab from "@/components/admin/SettingsTab";

// 🌟 Local Theme-Specific Custom Components
import BranchCategoriesTab from "./BranchCategoriesTab";
import BranchProductsTab from "./BranchProductsTab";
import BranchCustomersTab from "./BranchCustomersTab";
import BranchOrdersTab from "./BranchOrdersTab";
import BranchInventoryTab from "./BranchInventoryTab";
import BranchDailyCollectionTab from "./BranchDailyCollectionTab";
import BranchBarcodeScannerTab from "./BranchBarcodeScannerTab";
import BranchChatPanel from "./BranchChatPanel";
import BranchReviewsTab from "./BranchReviewsTab";
import BranchGalleryTab from "./BranchGalleryTab";
import BranchFraudTab from "./BranchFraudTab";
import BranchEmployeesTab from "./BranchEmployeesTab";
import BranchAttendanceTab from "./BranchAttendanceTab";
import AddProductModal from "./AddProductModal";
import AddCategoryModal from "./AddCategoryModal";

// 🌟 4 Separate Analytics Components
import CustomerAnalyticsView from "./analytics/CustomerAnalyticsView";
import OrderAnalyticsView from "./analytics/OrderAnalyticsView";
import ProductAnalyticsView from "./analytics/ProductAnalyticsView";
import CategoryAnalyticsView from "./analytics/CategoryAnalyticsView";

import { Loader2, RefreshCw, Building2 } from "lucide-react";

interface BranchDashboardProps {
  branchInfo: any;
  managerUser: any;
  subdomain: string;
  branchSlug: string;
  onLogout: () => void;
  products?: any[];
  onRefreshProducts?: () => void;
}

export default function BranchDashboard({
  branchInfo,
  managerUser,
  subdomain,
  branchSlug,
  onLogout,
}: BranchDashboardProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Modals state
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  // Isolated Branch Data States
  const [branchProducts, setBranchProducts] = useState<any[]>([]);
  const [branchCategories, setBranchCategories] = useState<any[]>([]);
  const [branchOrders, setBranchOrders] = useState<any[]>([]);
  const [branchCustomerCount, setBranchCustomerCount] = useState<number>(0);

  // Analytics Live States
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState<"7d" | "30d" | "90d">("30d");

  const primaryColor = branchInfo?.theme_config?.primary_color || "#059669";

  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("localhost") ||
      window.location.hostname === "127.0.0.1");

  const storageKey = `branch_auth_${subdomain}_${branchSlug}`;

  const fetchBranchProducts = useCallback(async () => {
    if (!branchInfo?.id) return;
    try {
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const url = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/products/?branch_id=${branchInfo.id}`
        : `/api/v1/products/?branch_id=${branchInfo.id}`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Branch-ID": String(branchInfo.id),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setBranchProducts(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Branch products fetch error:", err);
    }
  }, [branchInfo?.id, isLocal, storageKey, subdomain]);

  const fetchBranchCategories = useCallback(async () => {
    if (!branchInfo?.id) return;
    try {
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const url = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/stores/categories/?branch_id=${branchInfo.id}`
        : `/api/v1/stores/categories/?branch_id=${branchInfo.id}`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Branch-ID": String(branchInfo.id),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setBranchCategories(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Branch categories fetch error:", err);
    }
  }, [branchInfo?.id, isLocal, storageKey, subdomain]);

  const fetchBranchOrders = useCallback(async () => {
    if (!branchInfo?.id) return;
    try {
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const url = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/orders/?branch_id=${branchInfo.id}&admin=true`
        : `/api/v1/orders/?branch_id=${branchInfo.id}&admin=true`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Branch-ID": String(branchInfo.id),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setBranchOrders(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Branch orders fetch error:", err);
    }
  }, [branchInfo?.id, isLocal, storageKey, subdomain]);

  const fetchBranchCustomerCount = useCallback(async () => {
    if (!branchInfo?.id) return;
    try {
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const url = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/customers/?branch_id=${branchInfo.id}`
        : `/api/v1/customers/?branch_id=${branchInfo.id}`;

      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Branch-ID": String(branchInfo.id),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setBranchCustomerCount(list.length);
      }
    } catch (err) {
      console.error("Branch customer count fetch error:", err);
    }
  }, [branchInfo?.id, isLocal, storageKey, subdomain]);

  const fetchAnalyticsData = useCallback(
    async (section: string) => {
      if (!branchInfo?.id) return;
      setLoadingAnalytics(true);
      try {
        const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const token = authData.token || "";
        const days = analyticsTimeRange === "7d" ? 7 : analyticsTimeRange === "90d" ? 90 : 30;

        const url = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/ai/analytics/deep-dive/?section=${section}&days=${days}&branch_id=${branchInfo.id}`
          : `/api/v1/ai/analytics/deep-dive/?section=${section}&days=${days}&branch_id=${branchInfo.id}`;

        const res = await fetch(url, {
          headers: {
            Accept: "application/json",
            "X-Branch-ID": String(branchInfo.id),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.ok) {
          const result = await res.json();
          setAnalyticsData(result?.data || null);
        }
      } catch (err) {
        console.error("Analytics fetch error:", err);
      } finally {
        setLoadingAnalytics(false);
      }
    },
    [analyticsTimeRange, branchInfo?.id, isLocal, storageKey, subdomain]
  );

  const refreshAllBranchData = useCallback(() => {
    fetchBranchProducts();
    fetchBranchCategories();
    fetchBranchOrders();
    fetchBranchCustomerCount();
  }, [fetchBranchProducts, fetchBranchCategories, fetchBranchOrders, fetchBranchCustomerCount]);

  useEffect(() => {
    if (branchInfo?.id) {
      refreshAllBranchData();
    }
  }, [branchInfo?.id, refreshAllBranchData]);

  useEffect(() => {
    if (activeTab === "analytics_customer" || activeTab === "customer_intelligence") {
      fetchAnalyticsData("customer");
    } else if (activeTab === "analytics_orders" || activeTab === "orders_cashflow") {
      fetchAnalyticsData("orders");
    } else if (
      activeTab === "analytics_products" ||
      activeTab === "product_curves" ||
      activeTab === "product_ai_curves"
    ) {
      fetchAnalyticsData("products");
    } else if (activeTab === "analytics_categories" || activeTab === "category_analytics") {
      fetchAnalyticsData("categories");
    }
  }, [activeTab, fetchAnalyticsData]);

  const totalSales = branchOrders.reduce(
    (sum, o) => sum + (Number(o.total) || 0),
    0
  );
  const pendingOrders = branchOrders.filter(
    (o) => o.status === "pending"
  ).length;

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const isLight = theme === "light";

  const isAnalyticsActive = [
    "analytics_customer",
    "customer_intelligence",
    "analytics_orders",
    "orders_cashflow",
    "analytics_products",
    "product_curves",
    "product_ai_curves",
    "analytics_categories",
    "category_analytics",
  ].includes(activeTab);

  const analyticsTitle = useMemo(() => {
    if (activeTab === "analytics_customer" || activeTab === "customer_intelligence") {
      return "Customer Cohort Intelligence";
    }
    if (activeTab === "analytics_orders" || activeTab === "orders_cashflow") {
      return "Orders, Revenue & Cashflow";
    }
    if (
      activeTab === "analytics_products" ||
      activeTab === "product_curves" ||
      activeTab === "product_ai_curves"
    ) {
      return "Product Performance & AI Demand";
    }
    if (activeTab === "analytics_categories" || activeTab === "category_analytics") {
      return "Category Taxonomies & Benchmarks";
    }
    return "Branch Analytics & BI";
  }, [activeTab]);

  return (
    <div
      style={{ ["--primary" as any]: primaryColor }}
      className={`fixed inset-0 z-[99999] w-screen h-screen flex font-sans overflow-x-hidden ${
        theme === "light" ? "bg-[#f8fafc] text-slate-800" : "bg-slate-950 text-slate-100"
      }`}
    >
      {/* 🌟 Branch Sidebar */}
      <BranchSidebar
        branchSlug={branchSlug}
        branchName={branchInfo?.name}
        subdomain={subdomain}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        productCount={branchProducts.length}
        categoryCount={branchCategories.length}
        pendingOrders={pendingOrders}
        customerCount={branchCustomerCount}
        reviewCount={0}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={toggleTheme}
        primaryColor={primaryColor}
        isMobileOpen={isMobileSidebarOpen}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto w-full">
        <AdminHeader
          activeTab={activeTab}
          subdomain={subdomain}
          onNavigateTab={(tab) => setActiveTab(tab as any)}
          onRefresh={refreshAllBranchData}
          onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
        />

        <div className="flex-1 w-full p-4 sm:p-6 lg:p-8">
          {/* 1. Overview Tab */}
          {activeTab === "overview" && (
            <BranchOverviewTab
              branchInfo={branchInfo}
              productCount={branchProducts.length}
              ordersCount={branchOrders.length}
              salesTotal={totalSales}
              pendingOrdersCount={pendingOrders}
              theme={theme}
              primaryColor={primaryColor}
              onOpenProductModal={() => setIsProductModalOpen(true)}
              setActiveTab={setActiveTab}
            />
          )}

          {/* 2. Branch Products Tab */}
          {activeTab === "products" && (
            <BranchProductsTab
              products={branchProducts}
              subdomain={subdomain}
              categories={branchCategories}
              onOpenModal={() => setIsProductModalOpen(true)}
              onRefresh={fetchBranchProducts}
              branchId={branchInfo?.id}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 3. Branch Categories Tab */}
          {activeTab === "categories" && (
            <BranchCategoriesTab
              categories={branchCategories}
              subdomain={subdomain}
              onOpenModal={() => setIsCategoryModalOpen(true)}
              onRefresh={fetchBranchCategories}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 4. Branch Reviews Tab */}
          {activeTab === "reviews" && (
            <BranchReviewsTab
              branchInfo={branchInfo}
              subdomain={subdomain}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 5. Branch Media Gallery Tab */}
          {(activeTab === "gallery" || activeTab === "product_images" || activeTab === "images") && (
            <BranchGalleryTab
              subdomain={subdomain}
              products={branchProducts}
              branchId={branchInfo?.id}
              branchSlug={branchSlug}
              branchName={branchInfo?.name}
              onRefresh={fetchBranchProducts}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 6. Branch Orders Tab */}
          {activeTab === "orders" && (
            <BranchOrdersTab
              subdomain={subdomain}
              branchId={branchInfo?.id}
              branchName={branchInfo?.name || "Branch"}
              primaryColor={primaryColor}
              theme={theme}
            />
          )}

          {/* 7. COD Fraud Shield Tab */}
          {(activeTab === "fraud_detection" || activeTab === "fraud" || activeTab === "cod_fraud") && (
            <BranchFraudTab
              branchInfo={branchInfo}
              subdomain={subdomain}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 🌟 8. HR & STAFF - Branch Employees Tab */}
          {activeTab === "employees" && (
            <BranchEmployeesTab
              branchInfo={branchInfo}
              subdomain={subdomain}
              theme={theme}
              primaryColor={primaryColor}
              onNavigateToAttendance={() => setActiveTab("attendance")}
            />
          )}

          {/* 🌟 9. HR & STAFF - Attendance Daily Tab */}
          {activeTab === "attendance" && (
            <BranchAttendanceTab
              branchInfo={branchInfo}
              subdomain={subdomain}
              theme={theme}
              primaryColor={primaryColor}
            />
          )}

          {/* 10. Branch Live Chat Panel */}
          {(activeTab === "messages" ||
            activeTab === "chat" ||
            activeTab === "live_chat" ||
            activeTab === "branch_chat" ||
            activeTab === "chat_branch_merchant" ||
            activeTab === "chat_branch_customer") && (
            <BranchChatPanel
              subdomain={subdomain}
              branchId={branchInfo?.id}
              branchSlug={branchSlug}
              branchName={branchInfo?.name}
              theme={theme}
              primaryColor={primaryColor}
              initialChannel={
                activeTab === "chat_branch_customer" ? "branch_customer" : "branch_merchant"
              }
            />
          )}

          {/* 11. Branch Analytics Tabs */}
          {isAnalyticsActive && (
            <div className="space-y-6">
              <div
                className={`p-5 rounded-3xl border-2 flex flex-wrap items-center justify-between gap-4 transition-all ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-800 shadow-sm"
                    : "bg-slate-900/90 border-slate-800 text-white shadow-xl"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      backgroundColor: `${primaryColor}18`,
                      color: primaryColor,
                      borderColor: `${primaryColor}35`,
                    }}
                    className="w-12 h-12 rounded-2xl flex items-center justify-center border"
                  >
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base sm:text-lg font-black tracking-tight">{analyticsTitle}</h2>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-mono">
                        Outlet Mode
                      </span>
                    </div>
                    <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                      {branchInfo?.name || "Branch"} (ID: #{branchInfo?.id}) • Real-Time Database Analytics
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center p-1 rounded-2xl border ${
                      isLight ? "bg-slate-100 border-slate-200" : "bg-slate-950 border-slate-800"
                    }`}
                  >
                    {(["7d", "30d", "90d"] as const).map((range) => (
                      <button
                        key={range}
                        onClick={() => setAnalyticsTimeRange(range)}
                        style={analyticsTimeRange === range ? { backgroundColor: primaryColor, color: "#ffffff" } : {}}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer ${
                          analyticsTimeRange === range
                            ? "shadow-sm"
                            : isLight
                            ? "text-slate-600 hover:text-slate-900"
                            : "text-slate-400 hover:text-white"
                        }`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const sec = activeTab.includes("customer")
                        ? "customer"
                        : activeTab.includes("order") || activeTab.includes("cashflow")
                        ? "orders"
                        : activeTab.includes("product") || activeTab.includes("curve")
                        ? "products"
                        : "categories";
                      fetchAnalyticsData(sec);
                    }}
                    disabled={loadingAnalytics}
                    className={`p-2.5 rounded-2xl border transition-all cursor-pointer ${
                      isLight
                        ? "bg-white border-slate-200 hover:bg-slate-100 text-slate-700"
                        : "bg-slate-950 border-slate-800 hover:bg-slate-800 text-slate-300"
                    }`}
                    title="Refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingAnalytics ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>

              {loadingAnalytics ? (
                <div className="py-28 text-center">
                  <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin mx-auto mb-3" />
                  <p className="text-xs font-mono font-bold text-slate-400">Loading branch intelligence...</p>
                </div>
              ) : (
                <>
                  {(activeTab === "analytics_customer" || activeTab === "customer_intelligence") && (
                    <CustomerAnalyticsView
                      data={analyticsData}
                      isLight={isLight}
                      timeRange={analyticsTimeRange}
                      onRefresh={() => fetchAnalyticsData("customer")}
                    />
                  )}

                  {(activeTab === "analytics_orders" || activeTab === "orders_cashflow") && (
                    <OrderAnalyticsView
                      data={analyticsData}
                      isLight={isLight}
                      timeRange={analyticsTimeRange}
                      onRefresh={() => fetchAnalyticsData("orders")}
                    />
                  )}

                  {(activeTab === "analytics_products" ||
                    activeTab === "product_curves" ||
                    activeTab === "product_ai_curves") && (
                    <ProductAnalyticsView
                      data={analyticsData}
                      isLight={isLight}
                      timeRange={analyticsTimeRange}
                      onRefresh={() => fetchAnalyticsData("products")}
                    />
                  )}

                  {(activeTab === "analytics_categories" || activeTab === "category_analytics") && (
                    <CategoryAnalyticsView
                      data={analyticsData}
                      isLight={isLight}
                      timeRange={analyticsTimeRange}
                      onRefresh={() => fetchAnalyticsData("categories")}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* 12. POS Counter Scanner Tab */}
          {(activeTab === "barcode_scanner" ||
            activeTab === "barcode" ||
            activeTab === "barcode_stock_out" ||
            activeTab === "barcode-stock-out" ||
            activeTab === "barcode_stock" ||
            activeTab === "barcodeStockOut" ||
            activeTab === "pos_counter") && (
            <BranchBarcodeScannerTab
              branchInfo={branchInfo}
              subdomain={subdomain}
            />
          )}

          {/* 13. Daily Order Collection Tab */}
          {(activeTab === "daily_collection" ||
            activeTab === "daily-collection" ||
            activeTab === "dailyCollection" ||
            activeTab === "collection") && (
            <BranchDailyCollectionTab
              branchInfo={branchInfo}
              subdomain={subdomain}
              theme={theme}
            />
          )}

          {/* 14. Inventory Tab */}
          {(activeTab === "inventory" ||
            activeTab === "inventory_stock" ||
            activeTab === "inventory-stock" ||
            activeTab === "stock" ||
            activeTab === "warehouse") && (
            <BranchInventoryTab
              branchInfo={branchInfo}
              subdomain={subdomain}
            />
          )}

          {/* 15. Branch Customers Tab */}
          {activeTab === "customers" && (
            <BranchCustomersTab
              subdomain={subdomain}
              branchId={branchInfo?.id}
              branchName={branchInfo?.name || "Branch"}
              primaryColor={primaryColor}
              theme={theme}
            />
          )}

          {/* 16. Settings Tab */}
          {activeTab === "settings" && (
            <SettingsTab
              subdomain={subdomain}
              theme={theme}
            />
          )}
        </div>
      </main>

      {/* Modals */}
      <AddProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        subdomain={subdomain}
        categories={branchCategories}
        onSuccess={fetchBranchProducts}
        branchId={branchInfo?.id}
        theme={theme}
        primaryColor={primaryColor}
      />

      <AddCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSuccess={fetchBranchCategories}
        subdomain={subdomain}
        branchId={branchInfo?.id}
        primaryColor={primaryColor}
      />
    </div>
  );
}